import React, { useState, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid
} from "@mui/material/";
import { strings } from "../../../common/i18n";
import styles from "../styles";
import useColumnsRows from "./useColumnsRows";
import RightPanelTabs from "./RightPanelTabs";
import LeftPanelExclusions from "./LeftPanelExclusions";
import actions from "../../../Store/actions";
import LoadingButton from "@mui/lab/LoadingButton";
import { helpers } from "../../../common/lib";
import debounce from "lodash/debounce";
import { useExclusionDatePicker } from "../../../common/hooks";
import { logToSentry } from "../../../config/Sentry";

/**
 * Edit Audit Exclude Dialog Component
 */

const debounceMs = 300;
export default ({ onClose, open }) => {
    const dispatch = useDispatch();
    const { data, vinCheck } = useColumnsRows();
    const { auditAssets, audits, auth, typesList } = useSelector(
        (state) => state
    );

    const [loading, setLoading] = useState(false);
    const [exclusionReason, setExclusionReason] = useState(null);
    const [note, setNote] = useState("");
    const [identifiers, setIdentifiers] = useState("");
    const [selectedAssets, setSelectedAssets] = useState([]);
    const [tabValue, setTabValue] = useState(0);
    const [validList, setValidList] = useState({
        total: "",
        known: "",
        unknown: ""
    });
    const [foundList, setFoundList] = useState([]);
    const [selectedDate, setSelectedDate] = useState(Date.now());

    const getExclusionReasonName = (reasonId) =>
        typesList.exclusionReasonTypes.data.find(
            (item) => item.id === reasonId
        );

    /** Check if the exclusionReason id is sold  */
    const isSoldDate = useExclusionDatePicker(exclusionReason);

    /** On date change  */
    const handleDateChange = (date) => {
        const dt = date ? date.$d : new Date();
        const currentDate = new Date(dt);
        setSelectedDate(currentDate);
    };

    /** On dialog open */
    const onDialogEnter = () => dispatch(actions.assetSync.create.stop());

    /** On dialog close  */
    const onDialogClose = () => {
        if (loading) return;
        setExclusionReason(null);
        setIdentifiers("");
        setNote("");
        setSelectedAssets([]);
        dispatch(actions.assetSync.create.start());
        onClose && onClose();
    };

    /** On tab change  */
    const handleTabChange = (event, newValue) => {
        setIdentifiers("");
        setSelectedAssets([]);
        setTabValue(newValue);
    };

    const excludeAssetStatus = useMemo(
        () =>
            typesList.assetStatus.data.find((item) => item.name === "excluded"),
        [typesList]
    );

    /** On disable  */
    /** Correcting validatio for empty Submission Issue Fix  */
    const isDisabled = useMemo(() => {
        const exclusionReasonName = getExclusionReasonName(exclusionReason);
        const soldDate = new Date(selectedDate);
        const currentDate = new Date();

        if (
            exclusionReason &&
            note &&
            (identifiers.length > 0 || selectedAssets.length > 0)
        ) {
            if (exclusionReasonName.name !== "asset is sold") return false;
            /** If the asset is sold check for the date  */
            if (
                exclusionReasonName.name === "asset is sold" &&
                soldDate <= currentDate
            )
                return false;
            else return true;
        }

        return true;
    }, [selectedAssets, exclusionReason, note, identifiers, selectedDate]);

    /** Debounce for searching  */
    /** Adding debounce for the vins list check */
    const debouncedSearch = debounce(async (value) => {
        let vinList = [];

        /** split value based on the new line remove empty line if present  */
        const list = value.split(`\n`).filter((item) => item.length > 0);

        let validVin = 0;
        const found = [];
        list.map((item) => {
            const vin = vinCheck(item);
            if (vin.length > 0) {
                validVin++;
                found.push(vin[0]);
            }
            vinList.push(item);
        });
        setFoundList(found);
        setValidList({
            total: vinList.length,
            known: validVin,
            unknown: vinList.length - validVin
        });
    }, debounceMs);

    const onChangeState = useCallback(
        {
            exclusionReason: (val) => setExclusionReason(val),
            identifiers: (val) => {
                setIdentifiers(val);
                debouncedSearch(val);
            },
            note: (val) => setNote(val)
        },
        [debouncedSearch]
    );

    /** On change of identifiers,exclusion and note */
    const onChange = (name) => (event) => {
        try {
            const { value } = event.target;
            onChangeState[name](value);
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry(`On change ${name} submission:`, { error });
            dispatch(
                actions.toast.create.error(
                    strings("errors.onChangeEdit", { name })
                )
            );
        }
    };

    /** Get audit Asset id for the assets */
    const getAuditAssetId = (assetId) =>
        auditAssets.data.find((item) => item.asset === assetId).id;

    /** isAssigned length */

    const unCheckedData = useMemo(
        () => data.filter((item) => !item.isAssigned).length,
        [data]
    );

    /** On submitting the assets */
    const onSubmit = async () => {
        try {
            setLoading(true);

            const selectedAssetData = selectedAssets.map((item) =>
                helpers.getListItemForId(auditAssets, item)
            );
            const next = tabValue === 0 ? selectedAssetData : foundList;

            /** Check for the existing tab */
            const isvinListTab = tabValue === 1 ? true : false;

            const currentDate = new Date().toISOString();
            const auditId = audits.item.id;
            const assets = [];

            next.map((auditAsset) => {
                assets.push({
                    assetController: {
                        status: excludeAssetStatus.id, // Excluded asset status
                        assignedDate: currentDate,
                        auditAsset: isvinListTab
                            ? getAuditAssetId(auditAsset.id)
                            : auditAsset.id,
                        controller: auth.account.id,
                        exclusionReason: exclusionReason,
                        exclusionReasonDate: isSoldDate
                            ? new Date(selectedDate).toISOString()
                            : null
                    },
                    auditImages: [],
                    auditDocuments: [],
                    auditLocation: auditAsset.auditLocation,
                    notes: [
                        {
                            content: note,
                            createdDate: currentDate
                        }
                    ]
                });
            });

            const submissionData = {
                audit: auditId,
                createdDate: currentDate,
                auditAssetContent: assets
            };

            /** Submission post request previously there was a map function
             * for post request now its replaced  */

            await dispatch(
                actions.auditControllers.create.postRequest(submissionData)
            );
            await dispatch(actions.auditReview.create.getRequest(auditId));
            dispatch(
                actions.toast.create.success(strings("updatedSuccessfully"))
            );

            /** If threre is only 1 unchecked data
             * Then the previous data is processed
             * close the popup
             */
            if (unCheckedData === 1) onClose && onClose();

            /** Clear all state after submission */
            setExclusionReason(null);
            setIdentifiers("");
            setNote("");
            setSelectedAssets([]);
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On exclusion submission:", { error });
            dispatch(
                actions.toast.create.error(
                    strings("errors.onExclusionSubmission")
                )
            );
        }

        setLoading(false);
    };

    return (
        <Dialog
            fullWidth={true}
            maxWidth="md"
            onClose={onDialogClose}
            open={open}
            TransitionProps={{
                onEnter: onDialogEnter
            }}
        >
            <DialogContent>
                <DialogTitle>{strings("editUncheckedAssets")}</DialogTitle>
                <Divider />
            </DialogContent>
            <DialogContent>
                {loading ? (
                    <CircularProgress sx={styles.loadingIcon} />
                ) : (
                    <Grid sx={styles.noMarginBottom} container spacing={3}>
                        <Grid item xs={4}>
                            <LeftPanelExclusions
                                isSoldDate={isSoldDate}
                                handleDateChange={handleDateChange}
                                selectedDate={selectedDate}
                                validList={validList}
                                identifiers={identifiers}
                                note={note}
                                exclusionReason={exclusionReason}
                                onChange={onChange}
                            />
                        </Grid>
                        <Grid item xs={8}>
                            <RightPanelTabs
                                value={tabValue}
                                identifiers={identifiers}
                                selectedAssets={selectedAssets}
                                setSelectedAssets={setSelectedAssets}
                                onChange={onChange}
                                handleTabChange={handleTabChange}
                            />
                        </Grid>
                    </Grid>
                )}
            </DialogContent>
            <DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        size="large"
                        disabled={loading}
                        onClick={onDialogClose}
                    >
                        {strings("cancel")}
                    </Button>
                    <LoadingButton
                        onClick={onSubmit}
                        variant="outlined"
                        disabled={isDisabled}
                        size="large"
                        loading={loading}
                    >
                        {strings("submit")}
                    </LoadingButton>
                </DialogActions>
            </DialogContent>
        </Dialog>
    );
};
